import { Link } from 'react-router-dom';
import { Users, Award, Star } from 'lucide-react';
import { trustCountries } from '../data';

/**
 * TrustStrip — E-E-A-T credential strip directly below the hero.
 * Eager-loaded (above the fold on standard 900px-tall viewports).
 */
const TrustStrip = () => (
  <section
    className="relative -mt-12 z-10 bg-gradient-to-b from-slate-900 to-slate-950 border-y border-white/5 shadow-2xl"
    aria-labelledby="trust-strip-heading"
    data-testid="hero-trust-strip"
  >
    <h2 id="trust-strip-heading" className="sr-only">
      Verified credentials and global presence
    </h2>

    <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
      <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 py-6 md:py-7">
        <li className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-white/5 hover:border-[#C5A028]/40 transition-colors" data-testid="trust-tile-mea">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-500 via-white to-green-600 p-0.5 flex-shrink-0">
            <div className="w-full h-full rounded-full bg-slate-950 flex items-center justify-center text-lg" aria-hidden="true">🇮🇳</div>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">MEA · Govt of India</p>
            <p className="text-sm font-bold text-white leading-tight mt-1">Approved Agency</p>
            <p className="text-[10px] font-mono text-[#C5A028] leading-tight mt-0.5 truncate">RC: B-0762/DEL/PER/1000+/5/9148/2015</p>
          </div>
        </li>

        <li className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-white/5 hover:border-[#C5A028]/40 transition-colors" data-testid="trust-tile-placements">
          <div className="w-11 h-11 rounded-full bg-[#C5A028]/15 flex items-center justify-center flex-shrink-0 ring-1 ring-[#C5A028]/30">
            <Users className="w-5 h-5 text-[#C5A028]" aria-hidden="true" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">Track Record</p>
            <p className="text-lg font-bold text-white font-syne leading-tight mt-0.5">10,000+</p>
            <p className="text-[11px] text-slate-400 leading-tight">Successful Placements</p>
          </div>
        </li>

        <li className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-white/5 hover:border-[#C5A028]/40 transition-colors" data-testid="trust-tile-years">
          <div className="w-11 h-11 rounded-full bg-[#C5A028]/15 flex items-center justify-center flex-shrink-0 ring-1 ring-[#C5A028]/30">
            <Award className="w-5 h-5 text-[#C5A028]" aria-hidden="true" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">Experience</p>
            <p className="text-lg font-bold text-white font-syne leading-tight mt-0.5">15+ Years</p>
            <p className="text-[11px] text-slate-400 leading-tight">Trusted Since 2015</p>
          </div>
        </li>

        <li className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-white/5 hover:border-[#C5A028]/40 transition-colors" data-testid="trust-tile-embassy">
          <div className="w-11 h-11 rounded-full bg-green-900/40 flex items-center justify-center flex-shrink-0 ring-1 ring-green-900/60 text-lg" aria-hidden="true">
            🇸🇦
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">Royal Embassy</p>
            <p className="text-sm font-bold text-white leading-tight mt-0.5">Saudi Empanelled</p>
            <p className="text-[11px] text-slate-400 leading-tight">Direct GAMCA Channel</p>
          </div>
        </li>

        <li className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-white/5 hover:border-[#C5A028]/40 transition-colors col-span-2 sm:col-span-1" data-testid="trust-tile-reviews">
          <div className="w-11 h-11 rounded-full bg-[#C5A028]/15 flex items-center justify-center flex-shrink-0 ring-1 ring-[#C5A028]/30">
            <Star className="w-5 h-5 text-[#C5A028] fill-[#C5A028]" aria-hidden="true" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-none">Client Trust</p>
            <p className="text-sm font-bold text-white leading-tight mt-0.5">Verified Reviews</p>
            <Link to="/success-stories" className="text-[11px] text-[#C5A028] hover:underline leading-tight">
              Read success stories →
            </Link>
          </div>
        </li>
      </ul>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 py-4 border-t border-white/5" data-testid="trust-country-strip">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 whitespace-nowrap text-center sm:text-left">
          Active Deployments In
        </p>
        <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto no-scrollbar -mx-2 px-2 sm:mx-0 sm:px-0">
          {trustCountries.map((c) => (
            <Link
              key={c.name}
              to={c.href}
              className="group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/5 border border-white/5 hover:border-[#C5A028]/40 hover:bg-[#C5A028]/10 transition-all whitespace-nowrap"
              data-testid={`trust-flag-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="text-base leading-none" aria-hidden="true">{c.flag}</span>
              <span className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors">{c.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default TrustStrip;
